// The account page's session: signing up, in and out, unlocking the vault by passkey, recovery key or pairing, and
// the account's settings. No DOM here: the views call these and show what they return or throw.
import { Api, ApiError, type Tokens } from '../net/api.ts';
import { deleteAccount, signupOptions } from '../net/account.ts';
import {
  approvePairing,
  checkPairing,
  findPairing,
  listProjects,
  newVault,
  replaceRecoveryKey,
  startPairing,
  unlockWithPasskey,
  unlockWithRecovery,
  waitForPairing,
  wrapForPasskey,
  type OpenProject,
  type PairingRequest,
  type PairingToApprove,
} from '../net/vault.ts';
import { fingerprint, parseRecoveryKey, recoveryVerifier } from '../shared/crypto.ts';
import type { ExchangeKey } from '../shared/members.ts';
import { ensureExchangeKey } from '../net/sharing.ts';
import { browserName, sentence, type Activity, type PairingRef } from './format.ts';
import type { Keystore } from './keystore.ts';
import type { Limits, Usage } from './usage.ts';
import { createPasskey, PasskeyError, passkeyMessage, usePasskey } from './webauthn.ts';

type Bytes = Uint8Array<ArrayBuffer>;

export interface DeviceRow {
  id: string;
  name: string;
  kind: string;
  created_at: number;
  last_seen_at: number;
}

export interface PasskeyRow {
  id: string;
  label: string | null;
  device_type: string;
  backed_up: number;
  created_at: number;
  last_used_at: number | null;
  copies: number;
}

export interface AccountInfo {
  accountId: string;
  name: string;
  createdAt: number;
  thisDevice: string;
  devices: DeviceRow[];
  passkeys: PasskeyRow[];
  recoveryUpdatedAt: number | null;
  activity: Activity[];
  /** PROTOCOL.md §9 (relays from W8 on): the account's role, what it holds, and its limits (null: none, the owner's). */
  role?: 'owner' | 'writer';
  usage?: Usage;
  limits?: Limits;
}

export type Resumed = 'signedOut' | 'locked' | 'unlocked';

/** Why a signed-in browser is still locked, in the writer's words. */
export type LockReason = 'noPrf' | 'noCopy' | 'reload' | null;

/** What the writer should read for anything thrown here. */
/** What a refusal means to the writer, where the relay's own words wouldn't say what to do next. */
const API_MESSAGES: Record<string, string> = {
  // The passkey offered isn't one the relay knows: removed, made for another account, or a throwaway (the host
  // self-check's "five-rooms-test" looks like any other at sign-in; found in the Windows spike, 2026-10-15).
  unknownPasskey:
    'That passkey isn’t one Five Rooms knows: it may have been removed, or made for a test. Choose another passkey, or use your recovery key or pair with another device.',
};

export function messageOf(e: unknown): string {
  if (e instanceof ApiError) return API_MESSAGES[e.code] ?? sentence(e.message);
  if (e instanceof PasskeyError || e instanceof DOMException) return passkeyMessage(e);
  if (e instanceof TypeError) return 'Couldn’t reach Five Rooms. Check your connection and try again.';
  return e instanceof Error ? sentence(e.message) : String(e);
}

interface Recovering {
  accountId: string;
  recoveryToken: string;
  avk: Bytes;
}

type TokensReply = Tokens & { credentialId: string; vault: { id?: string; wrapped: string }[] };

export class Session {
  readonly api: Api;
  avk: Bytes | null = null;
  lockReason: LockReason = null;
  /** A passkey that signed in with a PRF output no vault copy opened: once the vault is open, it gets a copy. */
  private unmatched: { accountId: string; credentialId: string; prf: Bytes } | null = null;
  private recovering: Recovering | null = null;
  /** The account's exchange key (W9a, PROTOCOL.md §10.2), opened with the AVK: in memory only, like the AVK here. */
  private exchange: Promise<ExchangeKey | null> | null = null;

  constructor(
    readonly keystore: Keystore,
    origin: string,
    readonly device = { name: browserName(navigator.userAgent), kind: 'browser' },
  ) {
    this.api = new Api({ origin, client: 'browser', reauthenticate: () => this.confirmPasskey() });
  }

  /**
   * The passkey check the relay asks for before adding or removing a passkey, replacing the recovery key, signing another
   * device out, approving a pairing or taking a project off the web (PROTOCOL.md §3.4): an assertion through the unlock
   * endpoints, which record it for this device.
   */
  private async confirmPasskey(): Promise<void> {
    const { challengeId, options } = await this.api.call<{ challengeId: string; options: Record<string, unknown> }>('POST', '/api/vault/options', {});
    const passkey = await usePasskey(options);
    await this.api.call('POST', '/api/vault/verify', { challengeId, response: passkey.response });
  }

  get signedIn(): boolean {
    return !!this.api.tokens;
  }

  get accountId(): string {
    return this.api.tokens?.accountId ?? '';
  }

  get deviceId(): string {
    return this.api.tokens?.deviceId ?? '';
  }

  /** On load: the refresh cookie says whether this browser is signed in, the keystore whether it's unlocked. */
  async resume(): Promise<Resumed> {
    try {
      await this.api.refresh();
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) return 'signedOut';
      throw e;
    }
    this.avk = await this.keystore.recall(this.accountId);
    this.lockReason = this.avk ? null : 'reload';
    this.exchange = null;
    return this.avk ? 'unlocked' : 'locked';
  }

  async fingerprint(): Promise<string> {
    return this.avk ? fingerprint(this.avk) : '';
  }

  /** Holds the AVK, keeps it (unless memory-only) and gives a passkey that couldn't open it a copy of its own. */
  private async open(avk: Bytes): Promise<{ copyAdded: boolean }> {
    this.avk = avk;
    this.lockReason = null;
    this.exchange = null;
    await this.keystore.keep(this.accountId, avk);
    // An account without an exchange key gets one now (sign-up, or the first unlock since shared projects came).
    void this.exchangeKey().catch(() => {});
    let copyAdded = false;
    const u = this.unmatched;
    this.unmatched = null;
    if (u && u.accountId === this.accountId) {
      try {
        await this.api.call('PUT', `/api/passkeys/${encodeURIComponent(u.credentialId)}/vault`, {
          wrapped: await wrapForPasskey(avk, u.accountId, u.credentialId, u.prf),
        });
        copyAdded = true;
      } catch {
        // the passkey still signs in; the copy can be added another time
      }
    }
    return { copyAdded };
  }

  /** A new sign-in replaced a locked session on this browser: that session's device goes, so it isn't listed twice. */
  private async retire(oldDeviceId: string) {
    if (!oldDeviceId || oldDeviceId === this.deviceId) return;
    await this.api.call('DELETE', `/api/devices/${encodeURIComponent(oldDeviceId)}`).catch(() => {});
  }

  // Signing up and in ---------------------------------------------------------------------------------------------

  /** Creates the account and its vault; returns the recovery key to show once. `invite`: the code that lets this
   *  writer join (PROTOCOL.md §9.2; not needed on an empty relay, whose first account is the owner). */
  async signUp(name: string, invite: string | null = null): Promise<{ recoveryKey: string; passkeyUnlocks: boolean }> {
    const { challengeId, accountId, options } = await signupOptions(this.api, name, this.device, invite);
    const passkey = await createPasskey(options);
    const vault = await newVault(accountId, passkey.credentialId, passkey.prf);
    let reply: TokensReply;
    try {
      reply = await this.api.call<TokensReply>(
        'POST',
        '/api/signup/verify',
        { challengeId, response: passkey.response, device: this.device, client: 'browser', vault: vault.upload },
        false,
      );
    } catch (e) {
      // Refused after the passkey was made (an invite used by someone else meanwhile): the page says the browser may
      // have kept a passkey that signs in to nothing.
      if (e instanceof ApiError) Object.assign(e, { afterPasskey: true });
      throw e;
    }
    this.api.accept(reply);
    await this.open(vault.avk);
    return { recoveryKey: vault.recoveryKey, passkeyUnlocks: !!passkey.prf };
  }

  /** Signs in with a passkey; unlocks when its PRF output opens one of its vault copies. */
  async signIn(): Promise<{ unlocked: boolean }> {
    const previous = this.deviceId;
    const { challengeId, options } = await this.api.call<{ challengeId: string; options: Record<string, unknown> }>('POST', '/api/login/options', {}, false);
    const passkey = await usePasskey(options);
    const reply = await this.api.call<TokensReply>(
      'POST',
      '/api/login/verify',
      { challengeId, response: passkey.response, device: this.device, client: 'browser' },
      false,
    );
    this.api.accept(reply);
    await this.retire(previous);
    const avk = passkey.prf ? await unlockWithPasskey(reply.accountId, reply.credentialId, passkey.prf, reply.vault) : null;
    if (avk) {
      await this.open(avk);
      return { unlocked: true };
    }
    this.avk = null;
    this.lockReason = passkey.prf ? 'noCopy' : 'noPrf';
    if (passkey.prf) this.unmatched = { accountId: reply.accountId, credentialId: reply.credentialId, prf: passkey.prf };
    return { unlocked: false };
  }

  /** Unlocks this signed-in browser with a passkey, within its session: no new device, no new tokens. */
  async unlock(): Promise<{ unlocked: boolean }> {
    const { challengeId, options } = await this.api.call<{ challengeId: string; options: Record<string, unknown> }>('POST', '/api/vault/options', {});
    const passkey = await usePasskey(options);
    const reply = await this.api.call<{ credentialId: string; vault: { wrapped: string }[] }>('POST', '/api/vault/verify', {
      challengeId,
      response: passkey.response,
    });
    const avk = passkey.prf ? await unlockWithPasskey(this.accountId, reply.credentialId, passkey.prf, reply.vault) : null;
    if (avk) {
      await this.open(avk);
      return { unlocked: true };
    }
    this.lockReason = passkey.prf ? 'noCopy' : 'noPrf';
    if (passkey.prf) this.unmatched = { accountId: this.accountId, credentialId: reply.credentialId, prf: passkey.prf };
    return { unlocked: false };
  }

  // Recovery ------------------------------------------------------------------------------------------------------

  /**
   * Checks a recovery key and opens the vault with it. A signed-in browser is then unlocked; a signed-out one needs a
   * new passkey next (`finishRecovery`), since the recovery token can only add one.
   */
  async recover(text: string): Promise<{ unlocked: boolean }> {
    const secret = await parseRecoveryKey(text);
    const r = await this.api.call<{ accountId: string; recoveryToken: string; vault: { wrapped: string } }>(
      'POST',
      '/api/recover',
      { verifier: await recoveryVerifier(secret) },
      false,
    );
    let avk: Bytes;
    try {
      avk = await unlockWithRecovery(r.accountId, secret, r.vault.wrapped);
    } catch {
      throw new Error('That recovery key matches the account but didn’t open its vault. Try pairing with another device instead.');
    }
    if (this.signedIn) {
      if (r.accountId !== this.accountId) throw new Error('That recovery key belongs to a different account from the one signed in here.');
      await this.open(avk);
      return { unlocked: true };
    }
    this.recovering = { accountId: r.accountId, recoveryToken: r.recoveryToken, avk };
    return { unlocked: false };
  }

  /** After `recover`: a new passkey for this browser (with a vault copy when it gives a PRF output), which signs in. */
  async finishRecovery(): Promise<{ passkeyUnlocks: boolean }> {
    const rec = this.recovering ?? (() => { throw new Error('Enter your recovery key first.'); })();
    const auth = { authorization: `Bearer ${rec.recoveryToken}` };
    const { challengeId, options } = await this.api.call<{ challengeId: string; options: Record<string, unknown> }>(
      'POST',
      '/api/passkeys/options',
      {},
      false,
      auth,
    );
    const passkey = await createPasskey(options);
    const vault = passkey.prf ? { wrapped: await wrapForPasskey(rec.avk, rec.accountId, passkey.credentialId, passkey.prf) } : undefined;
    const reply = await this.api.call<TokensReply>(
      'POST',
      '/api/passkeys/verify',
      { challengeId, response: passkey.response, device: this.device, client: 'browser', ...(vault ? { vault } : {}) },
      false,
      auth,
    );
    this.api.accept(reply);
    this.recovering = null;
    await this.open(rec.avk);
    return { passkeyUnlocks: !!passkey.prf };
  }

  get recoveryPending(): boolean {
    return this.recovering !== null;
  }

  // Pairing -------------------------------------------------------------------------------------------------------

  startPairing(): Promise<PairingRequest> {
    return startPairing(this.api, this.device);
  }

  /** Waits for another device to approve; on approval this browser is signed in (as the new device) and unlocked. */
  async waitForPairing(req: PairingRequest, signal: AbortSignal): Promise<'approved' | 'denied' | 'cancelled'> {
    const previous = this.deviceId;
    let r;
    try {
      r = await waitForPairing(this.api, req, 1500, signal);
    } catch (e) {
      if (signal.aborted) return 'cancelled';
      throw e;
    }
    if (signal.aborted) return 'cancelled';
    if (r.status === 'denied') return 'denied';
    if (r.status !== 'approved') return 'cancelled';
    await this.retire(previous);
    await this.open(r.avk);
    return 'approved';
  }

  /** The request the writer scanned or typed, if its code matches; throws the mismatch message otherwise. */
  checkPairing(ref: PairingRef): Promise<PairingToApprove & { pub: Uint8Array }> {
    // Typed without its id (read off another screen), the request is found by its code.
    return ref.pairingId ? checkPairing(this.api, ref.pairingId, ref.code) : findPairing(this.api, ref.code);
  }

  async approvePairing(req: PairingToApprove & { pub: Uint8Array }): Promise<string> {
    if (!this.avk) throw new Error('Unlock this browser first.');
    return approvePairing(this.api, req, this.avk);
  }

  async denyPairing(pairingId: string): Promise<void> {
    await this.api.call('POST', `/api/pair/${encodeURIComponent(pairingId)}/deny`, {});
  }

  // Signing out ---------------------------------------------------------------------------------------------------

  /** Signs this browser out: the relay ends its session, and the vault key goes from memory and storage. */
  async signOut(): Promise<void> {
    try {
      if (this.signedIn) await this.api.call('POST', '/api/auth/logout', {});
    } catch {
      // signed out on the relay already, or unreachable: the local keys go regardless
    }
    await this.forgetLocally();
  }

  async forgetLocally(): Promise<void> {
    this.api.forget();
    this.avk = null;
    this.exchange = null;
    this.unmatched = null;
    this.recovering = null;
    await this.keystore.wipe();
  }

  /** Remember the vault key on this computer, or keep it in memory only. */
  async setMemoryOnly(on: boolean): Promise<void> {
    await this.keystore.setMemoryOnly(on, this.avk ? { accountId: this.accountId, avk: this.avk } : undefined);
  }

  // Shared projects -------------------------------------------------------------------------------------------------

  /**
   * The account's exchange key, opened with the AVK (made and uploaded when the account has none). Null while locked,
   * or when the relay's copy won't open with this AVK. Asked once per unlock; a failure (offline) is tried again.
   */
  exchangeKey(): Promise<ExchangeKey | null> {
    if (!this.avk || !this.signedIn) return Promise.resolve(null);
    const avk = this.avk;
    this.exchange ??= ensureExchangeKey(this.api, this.accountId, avk).catch((e) => {
      this.exchange = null;
      throw e;
    });
    return this.exchange;
  }

  // The account ---------------------------------------------------------------------------------------------------

  account(): Promise<AccountInfo> {
    return this.api.get<AccountInfo>('/api/account');
  }

  projects(): Promise<OpenProject[]> {
    if (!this.avk) return Promise.resolve([]);
    return listProjects(this.api, this.accountId, this.avk);
  }

  /** Takes a project off the web: the relay deletes everything it holds for it; devices keep their own copies. */
  async removeProject(projectId: string): Promise<void> {
    await this.api.call('DELETE', `/api/projects/${encodeURIComponent(projectId)}`);
  }

  async removeDevice(deviceId: string): Promise<void> {
    await this.api.call('DELETE', `/api/devices/${encodeURIComponent(deviceId)}`);
  }

  /** A new passkey for this account, with a vault copy when it gives a PRF output. */
  async addPasskey(): Promise<{ passkeyUnlocks: boolean }> {
    if (!this.avk) throw new Error('Unlock this browser first.');
    const { challengeId, options } = await this.api.call<{ challengeId: string; options: Record<string, unknown> }>('POST', '/api/passkeys/options', {});
    const passkey = await createPasskey(options);
    const vault = passkey.prf ? { wrapped: await wrapForPasskey(this.avk, this.accountId, passkey.credentialId, passkey.prf) } : undefined;
    await this.api.call('POST', '/api/passkeys/verify', {
      challengeId,
      response: passkey.response,
      device: this.device,
      client: 'browser',
      ...(vault ? { vault } : {}),
    });
    return { passkeyUnlocks: !!passkey.prf };
  }

  /** Removes a passkey; `signOutDevices`: and signs out the devices that signed in with it (not this one). */
  async removePasskey(credentialId: string, signOutDevices = false): Promise<{ signedOut: number }> {
    const r = await this.api.call<{ signedOut?: number }>('DELETE', `/api/passkeys/${encodeURIComponent(credentialId)}${signOutDevices ? '?signOut=devices' : ''}`);
    return { signedOut: r.signedOut ?? 0 };
  }

  /** Deletes this account and everything the relay holds for it (PROTOCOL.md §9.6), after a fresh passkey check;
   *  `confirm` is the account's name as the writer typed it. Then forgets it here (`forgetLocally`). */
  async deleteAccount(confirm: string): Promise<void> {
    await deleteAccount(this.api, confirm, async (options) => (await usePasskey(options)).response);
    await this.forgetLocally();
  }

  async replaceRecoveryKey(): Promise<string> {
    if (!this.avk) throw new Error('Unlock this browser first.');
    return replaceRecoveryKey(this.api, this.accountId, this.avk);
  }
}
